import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import { findChildElementWithMatchingValueType } from "features/configuration/GeneralConfiguration/utils.js";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import { useContext, useEffect, useMemo } from "react";
import { OrderTypes } from "@shared/features/trades/common/types";
import { ORDER_TYPE_TO_STRATEGY_ITEM_NAME } from "@shared/features/tradingTerminal/common/constants";
import useSharedTradeGeneralConfiguration from "@shared/features/tradingTerminal/common/hooks/useSharedTradeGeneralConfiguration";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { USER_CONFIGURATION_STRATEGY_VALUE } from "@shared/features/configuration/constants.ts";
import { DEFAULT_FULL_STRATEGY_TARGETS } from "@shared/features/bots/BotForm/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const useTradeGeneralConfiguration = (
  orderType: OrderTypes,
  includeConfigurationComponent = true,
  isForm?: boolean
) => {
  const { precisionConfig, isSignal, setIgnoreOrdersRatios } = useContext(
    TradingTerminalContext
  )!;
  const {
    generalOrdersData,
    generalOrdersDataStrategy,
    useTradingTerminalOldVersion,
  } = useSharedTradeGeneralConfiguration(orderType);

  const {
    generalConfiguration,
    getItemValueByName,
    getItemByName,
    getItemShouldDisplay,
    configurationModified,
    configurationId,
    itemsModified,
    setItemsModified,
    clonedConfiguration,
    updateConfiguration,
  } = useGeneralConfiguration({
    configuration: generalOrdersDataStrategy,
    showOverrides: false,
    colorModifiedValues: false,
    precisionConfig,
    useCard: false,
    useSimpleLayout: true,
    isForm,
    orderType,
    spacing: useTradingTerminalOldVersion ? 12 : 16,
  });

  const strategyItem = getItemByName(
    ORDER_TYPE_TO_STRATEGY_ITEM_NAME[orderType]
  );
  const numOrdersItem = getItemByName("Number of Orders");
  const selectedStrategyItem = findChildElementWithMatchingValueType(
    strategyItem,
    strategyItem?.value
  );
  const isUserConfigurationStrategy =
    isSignal && strategyItem?.value === USER_CONFIGURATION_STRATEGY_VALUE;
  const strategy: number[] = isUserConfigurationStrategy
    ? DEFAULT_FULL_STRATEGY_TARGETS
    : selectedStrategyItem?.value?.targets;

  const strategyId = isUserConfigurationStrategy
    ? USER_CONFIGURATION_STRATEGY_VALUE
    : Number(selectedStrategyItem?.value?.type);

  useEffect(() => {
    setIgnoreOrdersRatios(orderType, isUserConfigurationStrategy);
  }, [orderType, isUserConfigurationStrategy]);

  const priceRange = getItemValueByName("Price Range");

  const numOrdersShouldDisplay = numOrdersItem
    ? getItemShouldDisplay(numOrdersItem)
    : false;

  const numOrders = numOrdersShouldDisplay
    ? numOrdersItem.value
    : findLastNonZeroValue(strategy || []);

  let dependencyArray = [
    strategy,
    numOrders,
    configurationModified,
    configurationId,
    priceRange?.min,
    priceRange?.max,
  ];
  const data = useMemo(
    () => ({
      generalConfiguration: includeConfigurationComponent
        ? generalConfiguration
        : undefined,
      strategy,
      strategyId,
      priceRange: {
        min: parseFloat(priceRange?.min || 0),
        max: parseFloat(priceRange?.max || 0),
      },
      numOrders,
      configurationModified,
      configurationId,
      itemsModified,
      setItemsModified,
      clonedConfiguration,
      updateConfiguration,
    }),
    dependencyArray
  );

  if (orderType === OrderTypes.STOP) {
    const currentPrice = generalOrdersData?.current_price || 0;
    return {
      generalConfiguration: includeConfigurationComponent
        ? generalConfiguration
        : undefined,
      strategy: [100],
      priceRange: {
        min: currentPrice,
        max: currentPrice,
      },
      numOrders: 1,
      configurationModified: false,
      configurationId,
      itemsModified,
      setItemsModified,
      clonedConfiguration,
      updateConfiguration,
    };
  }

  return data;
};

export default useTradeGeneralConfiguration;
